import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, ArrowUp, MessageSquare, ArrowLeft } from "lucide-react";
import Banner from "../components/Banner";

export default function Category() {
  const { categoryId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cat, setCat] = useState(null);
  const [threads, setThreads] = useState([]);
  const [show, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/categories").then((r) => setCat(r.data.find((x) => x.id === categoryId)));
    api.get(`/threads?category_id=${categoryId}`).then((r) => setThreads(r.data));
  };
  useEffect(() => { load(); }, [categoryId]);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/threads", { title, content, category_id: categoryId });
      setShow(false); setTitle(""); setContent("");
      nav(`/thread/${data.id}`);
    } catch (e) { setErr(formatError(e.response?.data?.detail) || e.message); }
  };

  if (!cat) return <div className="p-8" data-testid="category-loading">Loading...</div>;

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold mb-4" data-testid="category-back-link">
          <ArrowLeft size={16} /> Back
        </Link>

        <div className="brutal-border brutal-shadow rounded-2xl p-8 mb-8"
          style={{ background: cat.color || "var(--primary)" }}>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Category</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-1">{cat.name}</h1>
          <p className="text-zinc-800 font-medium mt-2 text-lg">{cat.description}</p>
        </div>

        <div className="mb-6"><Banner position="top" /></div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold">{threads.length} threads</h2>
          {user ? (
            <Button onClick={() => setShow(!show)} data-testid="new-thread-btn"
              className="h-11 brutal-border brutal-shadow rounded-xl font-bold gap-2"
              style={{ background: "var(--ink)", color: "white" }}>
              <Plus size={16} /> New thread
            </Button>
          ) : (
            <Link to="/login" data-testid="login-to-post-link">
              <Button className="h-11 brutal-border brutal-shadow-sm rounded-xl font-bold bg-white hover:bg-white text-[var(--ink)]">
                Log in to post
              </Button>
            </Link>
          )}
        </div>

        {show && (
          <form onSubmit={create} data-testid="new-thread-form"
            className="brutal-border brutal-shadow rounded-xl p-6 mb-6 bg-white space-y-4 fade-up">
            <Input data-testid="thread-title-input" placeholder="Thread title" value={title}
              onChange={(e) => setTitle(e.target.value)} required
              className="h-12 brutal-border rounded-xl text-lg font-bold focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
            <Textarea data-testid="thread-content-input" placeholder="Write your post..." value={content}
              onChange={(e) => setContent(e.target.value)} required rows={5}
              className="brutal-border rounded-xl font-medium focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
            {err && <div className="text-sm font-bold text-[var(--destructive)]" data-testid="thread-error">{err}</div>}
            <div className="flex gap-2">
              <Button type="submit" data-testid="thread-submit-btn"
                className="brutal-border brutal-shadow rounded-xl font-bold"
                style={{ background: "var(--primary)", color: "var(--ink)" }}>
                Publish
              </Button>
              <Button type="button" onClick={() => setShow(false)}
                className="brutal-border brutal-shadow-sm rounded-xl font-bold bg-white hover:bg-white text-[var(--ink)]">
                Cancel
              </Button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {threads.length === 0 ? (
            <div className="brutal-border brutal-shadow-sm rounded-xl p-8 bg-white text-center text-zinc-600 font-medium">
              No threads yet. Be the first!
            </div>
          ) : threads.map((t, i) => (
            <Link key={t.id} to={`/thread/${t.id}`} data-testid={`thread-item-${t.id}`}
              className="block brutal-border brutal-shadow-sm rounded-xl p-5 bg-white brutal-hoverable fade-up"
              style={{ animationDelay: `${i * 30}ms` }}>
              <div className="flex items-start gap-4">
                <div className="brutal-border rounded-lg p-2 bg-[var(--accent)] text-center min-w-[50px]">
                  <ArrowUp size={14} className="mx-auto" strokeWidth={2.5} />
                  <div className="font-display font-black text-sm">{t.votes}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg leading-snug">{t.title}</h3>
                  <p className="text-sm text-zinc-600 line-clamp-2 mt-1 font-medium">{t.content}</p>
                  <div className="mt-2 text-xs text-zinc-500 flex items-center gap-3 font-medium">
                    <span>by <b className="text-zinc-700">{t.author_name}</b></span>
                    <span className="inline-flex items-center gap-1"><MessageSquare size={12} /> {t.reply_count}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
